/**
 * ============================================================================
 * WorkbenchStats — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: hero KPI strip at the top of the Supplier Workbench. Four oversized
 *          tiles: Total Active, Critical Risk Count, Awaiting Review,
 *          Requalification Due This Month.
 *
 * VISUAL: editorial style — large numeral, quiet label, no spurious deltas.
 *  We use the QualityMetricsDashboard StatTile look (14 px radius, 1 px border,
 *  subtle shadow). Tone is set per-tile via a band dot.
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SupplierWorkbench.module.css';
import type { SupplierWorkbenchStats } from './useSuppliersQuery';

export interface WorkbenchStatsProps {
  stats: SupplierWorkbenchStats | undefined;
  isLoading: boolean;
}

type Tone = 'neutral' | 'critical' | 'warn' | 'info';

interface Tile {
  key: keyof SupplierWorkbenchStats;
  labelKey: string;
  defaultLabel: string;
  tone: Tone;
}

const TILES: Tile[] = [
  {
    key: 'total_active',
    labelKey: 'supplier.workbench.stats.totalActive',
    defaultLabel: 'Total active suppliers',
    tone: 'neutral',
  },
  {
    key: 'critical_risk_count',
    labelKey: 'supplier.workbench.stats.criticalRisk',
    defaultLabel: 'Critical risk count',
    tone: 'critical',
  },
  {
    key: 'awaiting_review',
    labelKey: 'supplier.workbench.stats.awaitingReview',
    defaultLabel: 'Awaiting review',
    tone: 'warn',
  },
  {
    key: 'requalification_due_this_month',
    labelKey: 'supplier.workbench.stats.requalDue',
    defaultLabel: 'Requalification due this month',
    tone: 'info',
  },
];

const TONE_DOT: Record<Tone, string> = {
  neutral: styles.dotNeutral,
  critical: styles.dotCritical,
  warn: styles.dotWarn,
  info: styles.dotInfo,
};

export function WorkbenchStats({ stats, isLoading }: WorkbenchStatsProps) {
  const { t, i18n } = useTranslation();
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  if (isLoading) {
    return (
      <section
        className={styles.statsRow}
        aria-label={t('supplier.workbench.stats.regionAria', {
          defaultValue: 'Hero KPIs',
        })}
      >
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={styles.statSkeleton} aria-hidden="true" />
        ))}
      </section>
    );
  }

  return (
    <section
      className={styles.statsRow}
      aria-label={t('supplier.workbench.stats.regionAria', {
        defaultValue: 'Hero KPIs',
      })}
    >
      {TILES.map((tile) => {
        const value = stats ? stats[tile.key] : 0;
        const label = t(tile.labelKey, { defaultValue: tile.defaultLabel });
        return (
          <article
            key={tile.key}
            className={styles.statTile}
            aria-label={t('supplier.workbench.stats.tileAria', {
              defaultValue: '{{label}}: {{value}}',
              label,
              value: nf.format(value),
            })}
          >
            <header className={styles.statTileHeader}>
              <span className={styles.statTileLabel}>{label}</span>
              <span
                className={`${styles.statDot} ${TONE_DOT[tile.tone]}`}
                aria-hidden="true"
              />
            </header>
            <div className={styles.statTileValue}>{nf.format(value)}</div>
          </article>
        );
      })}
    </section>
  );
}
